import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowRight, CheckCircle2, Loader2, Video, DollarSign, Clock } from 'lucide-react';

const TestimonialSubmitPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    practiceName: '',
    email: '',
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!formData.fullName || !formData.practiceName || !formData.email) {
      setSubmitError('Please complete all required fields.');
      return;
    }

    if (!agreed) {
      setSubmitError('You must accept the agreements before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1');
      const apiUrl =
        process.env.REACT_APP_API_URL ||
        (isLocalhost ? 'http://localhost:8001' : '');
      const response = await fetch(`${apiUrl}/api/testimonial-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          agreed,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || 'Failed to submit');
      }

      setIsSuccess(true);
      window.open('https://calendly.com/asha-sales/customer-testimonial', '_blank');
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <Header />

      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      <main className="pt-24 pb-12 px-4 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50 via-white to-indigo-50/50"></div>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 -left-4 w-[500px] h-[500px] bg-purple-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-[500px] h-[500px] bg-indigo-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start justify-center">

            {/* Left Column: Value Prop */}
            <div className="flex-1 lg:pt-4 lg:sticky lg:top-28">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight text-slate-900 tracking-tight">
                Submit a{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Testimonial
                </span>
              </h1>

              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-lg">
                Share your experience with Asha and help other dental offices discover the power of AI. We'll pay you $500 if your testimonial is accepted.
              </p>

              {/* Benefits */}
              <div className="grid sm:grid-cols-1 gap-4 mb-8">
                {[
                  { icon: <Clock className="w-4 h-4 text-blue-600" />, title: '15-Minute Call', description: 'Quick and easy — we guide the conversation' },
                  { icon: <Video className="w-4 h-4 text-blue-600" />, title: 'We Handle Everything', description: 'We edit, brand, and produce the final clip' },
                  { icon: <DollarSign className="w-4 h-4 text-blue-600" />, title: '$500 Payment', description: 'Paid within 30 days of testimonial acceptance' },
                ].map((benefit, index) => (
                  <div key={index} className="flex gap-3 p-3 rounded-xl bg-white/60 border border-white/60 shadow-sm">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center border border-blue-100">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm mb-0.5">{benefit.title}</h3>
                      <p className="text-xs text-slate-500 leading-snug">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: Form */}
            <div className="w-full lg:w-[540px] flex-shrink-0 relative z-10">
              <Card className="shadow-2xl border-0 ring-1 ring-slate-900/5 bg-white/90 backdrop-blur-sm overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600"></div>
                <CardHeader className="pb-4 pt-6 px-6">
                  <CardTitle className="text-xl font-bold text-slate-900">
                    {isSuccess ? 'Success' : 'Get started'}
                  </CardTitle>
                  <CardDescription className="text-slate-500 text-sm mt-1">
                    {isSuccess
                      ? "Your testimonial was saved."
                      : "Fill out the form below and we'll get you scheduled."}
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-6 pb-6">
                  {isSuccess ? (
                    <div className="space-y-4 py-2 text-center">
                      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-emerald-100 bg-emerald-50">
                        <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-slate-900">
                          Your submission was received and stored successfully.
                        </p>
                        <p className="text-sm text-slate-500">
                          Calendly should open in a new tab. If it did not, use the button below.
                        </p>
                      </div>
                      <Button
                        type="button"
                        onClick={() => window.open('https://calendly.com/asha-sales/customer-testimonial', '_blank')}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10 text-base font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all"
                      >
                        Open Calendly
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <form className="space-y-3" onSubmit={handleSubmit}>
                      <div className="space-y-1.5">
                        <Label htmlFor="fullName" className="text-xs font-semibold text-slate-700">Full name *</Label>
                        <Input
                          id="fullName"
                          placeholder="Jane Doe"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          required
                          className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="practiceName" className="text-xs font-semibold text-slate-700">Practice name *</Label>
                        <Input
                          id="practiceName"
                          placeholder="Bright Smile Dental"
                          value={formData.practiceName}
                          onChange={handleInputChange}
                          required
                          className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="jane@practice.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-9 text-sm"
                        />
                      </div>

                      {/* Agreement Checkbox */}
                      <div className="flex items-start gap-2.5 pt-2">
                        <input
                          type="checkbox"
                          id="agreement"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          required
                          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                        <label htmlFor="agreement" className="text-xs text-slate-500 leading-relaxed cursor-pointer">
                          By submitting this form I accept the{' '}
                          <Link to="/terms-of-service" target="_blank" className="text-blue-600 hover:text-blue-700 underline">
                            Terms of Service
                          </Link>{' '}
                          and{' '}
                          <Link to="/marketing-service-agreement" target="_blank" className="text-blue-600 hover:text-blue-700 underline">
                            Marketing Service Agreement
                          </Link>.
                        </label>
                      </div>

                      {submitError && (
                        <p className="text-sm text-red-600" role="alert">
                          {submitError}
                        </p>
                      )}

                      <Button
                        type="submit"
                        disabled={isSubmitting || !agreed}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10 text-base font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit and Book My Call
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TestimonialSubmitPage;
